/**
 * Sets up axios interceptors so that:
 *  - GET requests are cached in localStorage when successful, and served from
 *    cache when the network fails (so the UI stays usable offline even if the
 *    Service Worker hasn't pre-cached that endpoint).
 *  - POST/PUT/DELETE writes are queued in IndexedDB when offline and a
 *    "fake" 202 response is returned so the UI doesn't crash.
 *  - When connection comes back, queued requests auto-replay.
 *
 * Uploads (multipart/form-data) are NOT queued because file blobs cannot be
 * serialized reliably — they need real network.
 */
import axios from 'axios';
import { enqueue, flushQueue, count } from './offlineQueue';
import { toast } from 'sonner';

const SAFE_METHODS = ['GET', 'HEAD', 'OPTIONS'];
const READ_CACHE_PREFIX = 'kj_read_cache:';
const READ_CACHE_MAX_BYTES = 250_000; // ~250KB por endpoint, evita reventar quota

let onlineState = typeof navigator !== 'undefined' ? navigator.onLine : true;
const listeners = new Set();
let pendingCount = 0;

function cacheKey(url) {
    // Normalizar: quitar host para que el cache sea por path+query
    try {
        const u = new URL(url, window.location.origin);
        return READ_CACHE_PREFIX + u.pathname + u.search;
    } catch {
        return READ_CACHE_PREFIX + url;
    }
}

function saveReadCache(url, data) {
    try {
        const payload = JSON.stringify({ data, ts: Date.now() });
        if (payload.length > READ_CACHE_MAX_BYTES) return; // muy grande, no cachear
        localStorage.setItem(cacheKey(url), payload);
    } catch (e) {
        // QuotaExceeded: limpiar entradas viejas y reintentar una vez
        try {
            const keys = Object.keys(localStorage).filter(k => k.startsWith(READ_CACHE_PREFIX));
            // borrar mitad mas vieja
            const items = keys.map(k => {
                try { return { k, ts: JSON.parse(localStorage.getItem(k)).ts || 0 }; }
                catch { return { k, ts: 0 }; }
            }).sort((a, b) => a.ts - b.ts);
            items.slice(0, Math.ceil(items.length / 2)).forEach(it => localStorage.removeItem(it.k));
            localStorage.setItem(cacheKey(url), JSON.stringify({ data, ts: Date.now() }));
        } catch (_) { /* ignore */ }
    }
}

function readCache(url) {
    try {
        const raw = localStorage.getItem(cacheKey(url));
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

/**
 * Update the locally cached read for a given API path. Useful for
 * optimistic UI updates while offline (e.g. after queueing a POST).
 * @param {string} apiPath - e.g. "/api/clientes"
 * @param {(data:any)=>any} updater - receives current cached data (or null) and returns new data
 */
export function patchReadCache(apiPath, updater) {
    try {
        const url = apiPath; // matches the same prefix used by interceptor
        const current = readCache(url);
        const next = updater(current ? current.data : null);
        saveReadCache(url, next);
    } catch (e) { /* ignore */ }
}

export function isOffline() {
    return !onlineState;
}

function notify() {
    listeners.forEach((cb) => {
        try { cb({ online: onlineState, pending: pendingCount }); } catch (e) { /* ignore */ }
    });
}

export function subscribeOnlineStatus(cb) {
    listeners.add(cb);
    cb({ online: onlineState, pending: pendingCount });
    return () => listeners.delete(cb);
}

async function refreshPendingCount() {
    try { pendingCount = await count(); } catch { pendingCount = 0; }
    notify();
}

async function tryFlush() {
    if (!onlineState) return;
    try {
        const result = await flushQueue(axios);
        if (result.ok > 0) {
            toast.success(`${result.ok} accion${result.ok > 1 ? 'es' : ''} sincronizada${result.ok > 1 ? 's' : ''}`);
        }
        if (result.conflict > 0) {
            toast.error(`${result.conflict} accion${result.conflict > 1 ? 'es' : ''} fallaron al sincronizar (validacion del servidor)`);
        }
        if (result.dropped > 0) {
            toast.error(`${result.dropped} accion${result.dropped > 1 ? 'es' : ''} descartada${result.dropped > 1 ? 's' : ''} tras varios intentos fallidos`);
        }
    } catch (e) {
        // ignore
    } finally {
        await refreshPendingCount();
    }
}

/**
 * Manual trigger to flush the queue. Returns the same {ok, failed, conflict, dropped}
 * as flushQueue, useful for the UI to show feedback.
 */
export async function manualFlush() {
    if (!onlineState) {
        toast.error('Sin conexion. Conectate a internet primero.');
        return null;
    }
    const result = await flushQueue(axios);
    if (result.ok > 0) toast.success(`${result.ok} accion${result.ok > 1 ? 'es' : ''} sincronizada${result.ok > 1 ? 's' : ''}`);
    if (result.failed > 0) toast.warning(`${result.failed} accion${result.failed > 1 ? 'es' : ''} pendiente${result.failed > 1 ? 's' : ''} de reintento`);
    if (result.conflict > 0) toast.error(`${result.conflict} fallaron por validacion del servidor`);
    if (result.dropped > 0) toast.error(`${result.dropped} descartada${result.dropped > 1 ? 's' : ''} tras varios intentos fallidos`);
    if (result.ok === 0 && result.failed === 0 && result.conflict === 0 && result.dropped === 0) {
        toast.info('Cola vacia. Nada por sincronizar.');
    }
    await refreshPendingCount();
    return result;
}

export function setupOfflineInterceptor() {
    // Keep online status in sync
    window.addEventListener('online', () => {
        onlineState = true;
        notify();
        tryFlush();
    });
    window.addEventListener('offline', () => {
        onlineState = false;
        notify();
    });

    refreshPendingCount();

    // Retry periodico: si estamos online y hay cola, intentar flush cada 30s.
    // Esto cubre casos donde el evento `online` no dispara o un flush previo
    // dejo items pendientes por errores transitorios.
    setInterval(() => {
        if (onlineState && pendingCount > 0) {
            tryFlush();
        }
    }, 30000);

    axios.interceptors.response.use(
        (res) => {
            // Cachear GETs exitosos a /api/* (excluyendo binarios, auth y uploads)
            try {
                const cfg = res.config || {};
                const method = (cfg.method || 'GET').toUpperCase();
                const url = cfg.url || '';
                const isApi = url.includes('/api/');
                const isAuth = url.includes('/api/auth/');
                const isUpload = url.includes('/api/uploads/') || url.includes('-pdf');
                const responseType = cfg.responseType || 'json';
                const fromCache = !!res.headers?.['x-from-cache'];

                if (method === 'GET' && isApi && !isAuth && !isUpload && responseType === 'json' && res.status >= 200 && res.status < 300) {
                    // Si hay escrituras pendientes en cola, el SW puede estar sirviendo
                    // una version vieja que no incluye nuestros optimistic updates.
                    // En ese caso preferimos el cache local mas reciente.
                    if (pendingCount > 0 && !fromCache) {
                        const cached = readCache(url);
                        if (cached) {
                            return { ...res, data: cached.data, headers: { ...res.headers, 'x-from-cache': 'local-pending' } };
                        }
                    } else if (!fromCache) {
                        saveReadCache(url, res.data);
                    }
                }
            } catch (e) { /* ignore */ }
            return res;
        },
        async (error) => {
            const cfg = error.config;
            const isNetworkError = !error.response;
            const method = (cfg?.method || 'GET').toUpperCase();
            const isWrite = !SAFE_METHODS.includes(method);
            const url = cfg?.url || '';
            const isApi = url.includes('/api/');
            const isFormData = cfg?.data instanceof FormData;
            const isAuth = url.includes('/api/auth/');
            const isLogin = url.includes('/api/auth/login');

            // FORZAR LOGOUT cuando el servidor invalida la sesion (cambio de password u otra causa).
            // Solo cuando hay respuesta del servidor con 401 y NO es el endpoint de login
            // (en login, 401 = credenciales malas, no sesion invalidada).
            if (!isNetworkError && error.response?.status === 401 && !isLogin && isApi) {
                const detail = error.response?.data?.detail || '';
                const seemsSessionInvalidated = /sesi[oó]n cerrada|cambio de contrase|token expirado|token inv[aá]lido|usuario desactivado|usuario no encontrado/i.test(detail);
                if (seemsSessionInvalidated && localStorage.getItem('token')) {
                    try {
                        localStorage.removeItem('token');
                        localStorage.removeItem('kj_user_cache');
                        delete axios.defaults.headers.common['Authorization'];
                        toast.error(detail || 'Sesion cerrada. Vuelve a iniciar sesion.', { duration: 5000 });
                    } catch (e) { /* ignore */ }
                    // Redirigir a login forzando recarga limpia
                    setTimeout(() => {
                        if (window.location.pathname !== '/login') {
                            window.location.href = '/login';
                        }
                    }, 600);
                    return Promise.reject(error);
                }
            }

            // FALLBACK GET desde localStorage cache cuando no hay red
            if (isNetworkError && method === 'GET' && isApi && !isAuth) {
                const cached = readCache(url);
                if (cached) {
                    return Promise.resolve({
                        data: cached.data,
                        status: 200,
                        statusText: 'OK (cache local)',
                        headers: { 'x-from-cache': 'local' },
                        config: cfg,
                        _fromLocalCache: true
                    });
                }
            }

            if (isNetworkError && isWrite && isApi && !isFormData && !isAuth) {
                // Queue the request
                try {
                    // Strip Authorization header — it'll be set fresh on replay
                    const { Authorization, ...restHeaders } = cfg.headers || {};
                    void Authorization;
                    await enqueue({
                        method,
                        url,
                        data: cfg.data,
                        headers: restHeaders
                    });
                    await refreshPendingCount();
                    toast.warning('Sin conexion. La accion se guardo y se sincronizara cuando vuelva el internet.', { duration: 4000 });
                    // Return a fake successful response so the UI keeps moving
                    return Promise.resolve({
                        data: { _offline: true, _queued: true },
                        status: 202,
                        statusText: 'Queued (offline)',
                        headers: {},
                        config: cfg
                    });
                } catch (e) {
                    return Promise.reject(error);
                }
            }
            if (isNetworkError && isFormData) {
                toast.error('Sin conexion. Las cargas de archivos requieren internet.');
            }
            return Promise.reject(error);
        }
    );
}
